<template>
  <VueMultiselect
    v-bind="vBind"
    v-on="vOn"
  >
    <template v-for="(_, slot) of $slots" :key="slot" v-slot:[slot]="scope">
      <slot :name="slot" v-bind="scope"/>
    </template>
  </VueMultiselect>
</template>

<script>
  import VueMultiselect from 'vue-multiselect';

  // Wrapper for Vue-Multiselect (Vue 3 compatible)

  export default {
    components: {
      VueMultiselect,
    },
    inheritAttrs: false,
    props: {
      modelValue: {
        type: [Object, Array, String, Number],
        default: null,
      },
      options: {
        type: Array,
        required: true,
      },
      placeholder: {
        type: String,
        default: '',
      },
      showNoResults: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: 'label',
      },
      trackBy: {
        type: String,
        default: 'value',
      },
      hideDropdown: {
        type: Boolean,
        default: false,
      },
      hideCaret: {
        type: Boolean,
        default: false,
      },
      simpleValue: {
        type: Boolean,
        default: false,
      },
      error: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    computed: {
      vBind() {
        // Filter out event listeners from $attrs (they start with 'on')
        const attrs = {};
        for (const key in this.$attrs) {
          if (!key.startsWith('on')) {
            attrs[key] = this.$attrs[key];
          }
        }
        const { class: userClass, style: userStyle, ...restAttrs } = attrs;
        return {
          ...restAttrs,
          modelValue: this.localValue,
          options: this.options,
          placeholder: this.placeholder,
          showNoResults: this.showNoResults,
          label: this.label,
          trackBy: this.trackBy,
          ...(userStyle !== undefined ? { style: userStyle } : {}),
          class: [
            userClass,
            {
              'multiselect--no-dropdown': this.hideDropdown,
              'multiselect--no-caret': this.hideCaret,
              'multiselect--error': this.error,
            },
          ],
        };
      },
      vOn() {
        // Forward event listeners from $attrs
        const listeners = {};
        for (const key in this.$attrs) {
          if (key.startsWith('on') && typeof this.$attrs[key] === 'function') {
            // Convert onSomeEvent to some-event format for v-on
            const eventName = key.slice(2).replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
            listeners[eventName] = this.$attrs[key];
          }
        }
        return {
          ...listeners,
          'update:modelValue': (value) => this.localValue = value,
        };
      },
      // This acts as a wrapper for the value that and converts it depending on simpleValue prop:
      // False -> Value is the whole option object, default Vue-Multiselect behavior.
      // True  -> Value is just the option property defined by trackBy.
      localValue: {
        get() {
          return this.simpleValue
            ? this.options.find((option) => option[this.trackBy] === this.modelValue)
            : this.modelValue;
        },
        set(newValue) {
          const emitValue = this.simpleValue
            ? (newValue !== null ? newValue[this.trackBy] : null)
            : newValue;
          this.$emit('update:modelValue', emitValue)
        }
      }
    },
  };
</script>
